import { Banner, Button, ButtonGroup, Card, Modal, Select, Tabs, TextField, type TabProps } from '@shopify/polaris';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { PlacementTab } from '../components/ManageWidgets/tabs/placement.tab';
import { DesignTab } from '../components/ManageWidgets/tabs/design.tab';
import { Widget } from '../widgets/widgets';
import { useWidgetStore, type WidgetConfig } from '../store/widget.store';
import { useShallow } from 'zustand/react/shallow';
import { ContentTab } from '../components/ManageWidgets/tabs/content.tab';
import { Mutations, Queries } from '../query/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast, type Id as ToastID } from 'react-toastify';
import { addEmptyOption } from '../utils/select';
import { useSearchParams } from 'react-router-dom';

const tabs: TabProps[] = [
    {
        id: 'content',
        content: 'Content',
        accessibilityLabel: 'Content',
    },
    {
        id: 'design',
        content: 'Design',
        accessibilityLabel: 'Design',
    },
    {
        id: 'placement',
        content: 'Placement',
        accessibilityLabel: 'Placement',
    },
];

let widgetReference: Widget | null = null;

export function ManageWidgetPage() {
    const [selected, setSelected] = useState(0);
    const widgetHandler = useRef<HTMLDivElement>(null);
    const createToast = useRef<ToastID>(null);
    const updateToast = useRef<ToastID>(null);
    const deleteToast = useRef<ToastID>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        verticalPadding,
        horizontalPadding,
        borderRadius,
        borderWidth,
        borderColor,
        backgroundColor,
        fixedDate,
        fixedMinutes,
        endText,
        onFinish,
        timerType,
        name,
        _id,
        active,
        setName,
        getConfig,
        setWidget,
        setId,
        resetState,
        cloneWidget,
        fullWidth,
        resumeSessionTimer,
        titleText,
        freeShippingThreshold,
        topMargin,
        bottomMargin,
        widgetType,
        setWidgetType,
        scrollingText,
        gradientEnabled,
        gradientColor,
        gradientDirection,
        shadowEnabled,
        shadowIntensity,
        animationStyle,
    } = useWidgetStore(
        useShallow(state => ({
            verticalPadding: state.verticalPadding,
            horizontalPadding: state.horizontalPadding,
            borderRadius: state.borderRadius,
            borderWidth: state.borderWidth,
            borderColor: state.borderColor,
            backgroundColor: state.backgroundColor,
            fixedDate: state.fixedDate,
            fixedMinutes: state.fixedMinutes,
            endText: state.endText,
            onFinish: state.onFinish,
            timerType: state.timerType,
            name: state.name,
            _id: state._id,
            active: state.active,
            setName: state.setName,
            getConfig: state.getConfig,
            setWidget: state.setWidget,
            setId: state.setId,
            resetState: state.resetState,
            cloneWidget: state.cloneWidget,
            fullWidth: state.fullWidth,
            resumeSessionTimer: state.resumeSessionTimer,
            titleText: state.titleText,
            freeShippingThreshold: state.freeShippingThreshold,
            topMargin: state.topMargin,
            bottomMargin: state.bottomMargin,
            widgetType: state.widgetType,
            setWidgetType: state.setWidgetType,
            scrollingText: state.scrollingText,
            gradientEnabled: state.gradientEnabled,
            gradientColor: state.gradientColor,
            gradientDirection: state.gradientDirection,
            shadowEnabled: state.shadowEnabled,
            shadowIntensity: state.shadowIntensity,
            animationStyle: state.animationStyle,
        }))
    );

    const widget = useMemo(() => {
        const w = new Widget({
            verticalPadding,
            horizontalPadding,
            borderRadius,
            borderWidth,
            borderColor,
            backgroundColor,
            titleText,
            fixedDate,
            fixedMinutes,
            endText,
            onFinish,
            timerType,
            fullWidth,
            resumeSessionTimer,
            freeShippingThreshold,
            topMargin,
            bottomMargin,
            scrollingText,
            gradientEnabled,
            gradientColor,
            gradientDirection,
            shadowEnabled,
            shadowIntensity,
            animationStyle,
        });
        if (widgetReference) {
            widgetReference.destroy();
        }
        widgetReference = w;
        return w;
    }, [fixedDate, fixedMinutes, onFinish, timerType, fullWidth, resumeSessionTimer, widgetType]);

    const { data: widgetsList, isLoading: isFetchingWidgets } = useQuery<WidgetConfig[]>({
        queryKey: [Queries.WIDGET_GET_ALL],
    });

    const {
        mutate: createWidget,
        isError: isCreateError,
        isSuccess: isCreateSuccess,
        error: createError,
        data: createData,
        isPending: isCreatePending,
    } = useMutation<{ id: string }, unknown, WidgetConfig>({
        mutationKey: [Mutations.WIDGET_CREATE],
    });

    const {
        mutate: updateWidget,
        isError: isUpdateError,
        isSuccess: isUpdateSuccess,
        isPending: isUpdatePending,
        error: updateError,
    } = useMutation<unknown, unknown, WidgetConfig>({
        mutationKey: [Mutations.WIDGET_UPDATE],
    });

    const {
        mutate: deleteWidget,
        isError: isDeleteError,
        isSuccess: isDeleteSuccess,
        isPending: isDeletePending,
        error: deleteError,
    } = useMutation<unknown, unknown, { _id: string }>({
        mutationKey: [Mutations.WIDGET_DELETE],
    });

    useEffect(() => {
        widget.setStyle({
            verticalPadding,
            horizontalPadding,
            borderRadius,
            borderWidth,
            borderColor,
            backgroundColor,
            fixedDate,
            fixedMinutes,
            onFinish,
            timerType,
            fullWidth,
            freeShippingThreshold,
            topMargin,
            bottomMargin,
            gradientEnabled,
            gradientColor,
            gradientDirection,
            shadowEnabled,
            shadowIntensity,
            animationStyle,
        });
    }, [
        verticalPadding,
        horizontalPadding,
        borderRadius,
        borderWidth,
        borderColor,
        backgroundColor,
        titleText,
        fixedDate,
        fixedMinutes,
        endText,
        onFinish,
        timerType,
        fullWidth,
        freeShippingThreshold,
        topMargin,
        bottomMargin,
        gradientEnabled,
        gradientColor,
        gradientDirection,
        shadowEnabled,
        shadowIntensity,
        animationStyle,
    ]);

    useEffect(() => {
        setTimeout(() => setWidgetType(widget.widgetType), 0);
    }, [titleText, widget]);

    useEffect(() => {
        widget.setContent({ text: titleText, endText, scrollingText });
    }, [titleText, endText, scrollingText]);

    useEffect(() => {
        if (widgetHandler.current) {
            widgetHandler.current?.replaceChildren();
            widgetHandler.current?.appendChild(widget.getWidgetElement());
        }
    }, [widgetHandler.current, widget]);

    useEffect(() => {
        if (isCreateError) {
            toast.update(createToast.current as ToastID, {
                render: 'Error creating widget: ' + (createError as Error).message,
                type: 'error',
                isLoading: false,
                autoClose: 10000,
            });
        }
    }, [isCreateError]);

    useEffect(() => {
        if (isCreateSuccess) {
            toast.update(createToast.current as ToastID, {
                render: 'Widget created successfully',
                type: 'success',
                isLoading: false,
                autoClose: 5000,
            });
            if (createData?.id) {
                setId(createData.id);
                setSearchParams({ id: createData.id });
            } else {
                console.error('[MWP] Created widget id is missing');
            }
        }
    }, [isCreateSuccess, createData]);

    useEffect(() => {
        if (isUpdateError) {
            toast.update(updateToast.current as ToastID, {
                render: 'Error updating widget: ' + (updateError as Error).message,
                type: 'error',
                isLoading: false,
                autoClose: 10000,
            });
        }
    }, [isUpdateError]);

    useEffect(() => {
        if (isUpdateSuccess) {
            toast.update(updateToast.current as ToastID, {
                render: 'Widget updated successfully',
                type: 'success',
                isLoading: false,
                autoClose: 5000,
            });
        }
    }, [isUpdateSuccess]);

    useEffect(() => {
        if (isDeleteError) {
            toast.update(deleteToast.current as ToastID, {
                render: 'Error deleting widget: ' + (deleteError as Error).message,
                type: 'error',
                isLoading: false,
                autoClose: 10000,
            });
        }
    }, [isDeleteError]);

    useEffect(() => {
        if (isDeleteSuccess) {
            toast.update(deleteToast.current as ToastID, {
                render: 'Widget deleted successfully',
                type: 'success',
                isLoading: false,
                autoClose: 5000,
            });
            resetState();
            setSearchParams({ id: '' });
        }
    }, [isDeleteSuccess]);

    useEffect(() => {
        const widgetId = searchParams.get('id');
        if (widgetsList?.length && (_id || widgetId)) {
            const widget = widgetsList.find(w => w._id === (widgetId || _id)) as WidgetConfig;

            if (widget) {
                setWidget(widget);
            } else {
                console.error(`[MWP] Widget with id ${widgetId || _id} not found`);
            }
        }
    }, [widgetsList]);

    const createWidgetHandler = () => {
        if (_id) {
            updateWidget(getConfig());
            updateToast.current = toast('Updating widget...', { autoClose: false });
        } else {
            createWidget(getConfig());
            createToast.current = toast('Creating widget...', { autoClose: false });
        }
    };

    const changeStatusHandler = () => {
        if (_id) {
            updateWidget({ ...getConfig(), active: !active });
            updateToast.current = toast(`${active ? 'Deactivating' : 'Activating'} widget...`, { autoClose: false });
        } else {
            console.error('[MWP] Cannot change widget status without id');
        }
    };

    const deleteWidgetHandler = () => {
        if (_id) {
            deleteWidget({ _id });
            deleteToast.current = toast('Deleting widget...', { autoClose: false });
        } else {
            console.error('[MWP] Cannot delete widget without id');
        }
    };

    const handleTabChange = useCallback((selectedTabIndex: number) => setSelected(selectedTabIndex), []);

    const selectWidgetHandler = (id: string) => {
        if (widgetsList && widgetsList.length > 0) {
            const widget = widgetsList.find(w => w._id === id) as WidgetConfig;

            if (widget) {
                setWidget(widget);
                setSearchParams({ id });
            } else {
                console.error(`[MWP] Widget with id ${id} not found`);
            }
        }
    };

    return (
        <div className="mv-manage-page">
            <div className="mv-manage-toolbar mv-reveal">
                <div className="mv-manage-toolbar-group">
                    <div className="mv-manage-field">
                        <Select
                            options={addEmptyOption(
                                widgetsList?.map(widget => ({ label: widget.name, value: widget._id as string })) || []
                            )}
                            label="Select widget"
                            disabled={isFetchingWidgets || !widgetsList?.length}
                            onChange={selectWidgetHandler}
                            value={_id || ''}
                        />
                    </div>

                    <div className="mv-manage-field">
                        <TextField label="Widget name" autoComplete="off" value={name} onChange={v => setName(v)} />
                    </div>
                </div>

                <div className="mv-manage-actions">
                    <ButtonGroup>
                        <Button
                            variant="primary"
                            tone="success"
                            onClick={createWidgetHandler}
                            loading={isCreatePending || isUpdatePending}
                        >
                            Save
                        </Button>
                        <Button variant="secondary" tone="success" disabled={!_id} onClick={cloneWidget}>
                            Clone
                        </Button>
                        <Button
                            variant="primary"
                            tone="critical"
                            disabled={!_id}
                            onClick={() => setShowDeleteModal(true)}
                            loading={isDeletePending}
                        >
                            Delete
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            {_id ? (
                <div className="mv-status-banner mv-reveal" style={{ animationDelay: '120ms' }}>
                    {active ? (
                        <Banner tone="success" title={`${name} is live`}>
                            <p>Customers can currently see this widget in all selected placements.</p>
                            <div style={{ marginTop: '10px' }}>
                                <Button
                                    tone="critical"
                                    variant="primary"
                                    onClick={changeStatusHandler}
                                    loading={isUpdatePending}
                                >
                                    Pause widget
                                </Button>
                            </div>
                        </Banner>
                    ) : (
                        <Banner tone="warning" title={`${name} is paused`}>
                            <p>This widget is saved but hidden from your storefront right now.</p>
                            <div style={{ marginTop: '10px' }}>
                                <Button
                                    tone="success"
                                    variant="primary"
                                    onClick={changeStatusHandler}
                                    loading={isUpdatePending}
                                >
                                    Activate widget
                                </Button>
                            </div>
                        </Banner>
                    )}
                </div>
            ) : null}

            <div className="mv-manage-canvas mv-widget-editor-controls mv-reveal" style={{ animationDelay: '220ms' }}>
                <div className="mv-manage-preview">
                    <div className="mv-preview-card">
                        <div className="mv-preview-header">
                            <p className="mv-preview-title">Live preview</p>
                            <p className="mv-preview-subtitle">Storefront widget rendering</p>
                        </div>
                        <div
                            ref={widgetHandler}
                            className={`mv-preview-body${fullWidth ? '' : ' mv-preview-body--contained'}`}
                        ></div>
                    </div>
                </div>

                <div className="mv-manage-editor mv-widget-editor-controls-tab">
                    <Card>
                        <div className="mv-editor-tabs">
                            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
                                <div className="mv-editor-content">
                                    {tabs[selected]?.id === 'content' && <ContentTab widgetType={widgetType} />}
                                    {tabs[selected]?.id === 'design' && <DesignTab widgetType={widgetType} />}
                                    {tabs[selected]?.id === 'placement' && <PlacementTab widgetType={widgetType} />}
                                </div>
                            </Tabs>
                        </div>
                    </Card>
                </div>
            </div>

            <Modal
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                }}
                title="Confirm deletion"
                size="small"
                primaryAction={{
                    content: 'Delete',
                    onAction: () => {
                        setShowDeleteModal(false);
                        deleteWidgetHandler();
                    },
                    destructive: true,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: () => {
                            setShowDeleteModal(false);
                        },
                    },
                ]}
            >
                <p style={{ padding: 20 }}>Are you sure you want to delete this widget?</p>
            </Modal>
        </div>
    );
}
